import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';

/**
 * Session gate for Shell routes — Field Notes rebuild (epic #950 PR-8,
 * formerly the console-shared `createRequireAuth` factory
 * instantiation). Loading probes render a full-viewport status line;
 * unauthenticated visitors bounce to /login with the origin path in
 * router state so Login can return them after sign-in.
 */
interface Props {
  readonly children: ReactNode;
}

export function RequireAuth({ children }: Props): JSX.Element {
  const { status } = useSession();
  const location = useLocation();
  const t = useT();

  if (status === 'loading') {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-canvas px-8 text-body text-ink-low"
        role="status"
        aria-live="polite"
      >
        <p>{t('common.session.checking')}</p>
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}
